import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ActiveCoverage, ActiveCoverageService } from '@lib/insurance-packages/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { of, switchMap } from 'rxjs';

import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { LoaderComponent, ModalHeaderComponent } from '@lib/shared-ui/components/layout';

import { MemberCoverageOptionsComponent } from '../member-coverage-options/member-coverage-options.component';

export interface MemberCoverageDialogData {
  userId: string;
}

export const MEMBER_COVERAGE_DIALOG_CONFIG = { width: '1100px', maxWidth: '95vw', maxHeight: '90vh', autoFocus: false };

@Component({
  selector: 'tasks-member-coverage-dialog',
  standalone: true,
  imports: [ModalHeaderComponent, LoaderComponent, MemberCoverageOptionsComponent, MatIcon, MatTooltip, TranslatePipe],
  templateUrl: './member-coverage-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberCoverageDialogComponent {
  private readonly activeCoverageService = inject(ActiveCoverageService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly data = inject<MemberCoverageDialogData>(MAT_DIALOG_DATA);
  protected readonly formatDate = formatDate;

  protected readonly coverage = signal<ActiveCoverage | null>(null);
  protected readonly loading = signal<boolean>(true);
  protected readonly error = signal<boolean>(false);

  constructor() {
    this.loadCoverage();
  }

  protected loadCoverage(): void {
    this.loading.set(true);
    this.error.set(false);

    this.activeCoverageService
      .getUserPackage(this.data.userId)
      .pipe(
        switchMap(memberPackage => (memberPackage?.id ? this.activeCoverageService.getActiveCoverage(memberPackage.id) : of(null))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: coverage => {
          this.coverage.set(coverage);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }
}
